// ============================================
// LIGA AJUPAM - League Context Provider (v6.5.0 Fase 3 / v6.7.21)
// ============================================
// Contexto global que expone la liga activa del admin + setter.
//
// - Admins (LEAGUE_ADMIN, ADMIN, SUPER_ADMIN): la lista de ligas
//   administrables viene de `admin_get_my_leagues`. La liga activa se
//   persiste en localStorage bajo `active_league_id` para rehidratar.
//   Si no hay selección guardada, se elige la primera enabled.
// - Usuarios públicos y participantes: este provider NO se monta
//   (existen sólo en flujos admin). Para ese flujo, ver:
//   - getCurrentLeague() / getLeagueBySlug() (pública)
//   - team.league_id (participante autenticado)
//
// v6.7.21: eliminado `is_current` (la columna se droppeó en la migración).
//          El fallback "primera con is_current" pasó a "primera enabled".

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AdminLeague } from '../types';
import { getMyLeagues } from '../services/api/leagues';

const STORAGE_KEY = 'active_league_id';

interface LeagueContextValue {
  /** Ligas que el admin puede administrar (vacío para usuarios no-admin). */
  availableLeagues: AdminLeague[];
  /** Liga activa seleccionada por el admin. Null si no hay admin logueado. */
  currentLeague: AdminLeague | null;
  /** Cambia la liga activa y persiste en localStorage. */
  setCurrentLeague: (leagueId: string) => void;
  /** Refresca la lista desde el backend (p. ej. tras crear una liga nueva). */
  refreshLeagues: () => Promise<void>;
  /** True mientras se hace el primer fetch. */
  isLoading: boolean;
  /**
   * Token de sesión admin activo (o null). Expuesto para componentes compartidos
   * que necesitan autenticar RPCs/edge functions admin sin threadear el token por
   * props (ej. el modal de historial del jugador). — v6.11.13
   */
  adminSessionToken: string | null;
}

const LeagueContext = createContext<LeagueContextValue | undefined>(undefined);

interface LeagueProviderProps {
  /** Token de sesión admin. Null si no hay admin logueado. */
  adminSessionToken: string | null;
  children: React.ReactNode;
}

export const LeagueProvider: React.FC<LeagueProviderProps> = ({ adminSessionToken, children }) => {
  const [availableLeagues, setAvailableLeagues] = useState<AdminLeague[]>([]);
  const [currentLeagueId, setCurrentLeagueId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchLeagues = useCallback(async () => {
    if (!adminSessionToken) {
      setAvailableLeagues([]);
      return;
    }
    setIsLoading(true);
    try {
      const leagues = await getMyLeagues(adminSessionToken);
      setAvailableLeagues(leagues);

      setCurrentLeagueId((prev) => {
        if (prev) {
          const prevLeague = leagues.find((l) => l.league_id === prev);
          // v6.11.9.08 — Respetar CUALQUIER liga seleccionada que siga existiendo,
          // sin importar su estado (enabled/draft/archived). Antes se rebotaba
          // archived/draft → primera enabled, lo que impedia consultar el
          // historico: al entrar a la 4ta (archivada) la app saltaba a la 5ta y
          // mostraba sus categorias/datos. La eleccion explicita del admin manda
          // y se persiste entre recargas. Si el ID guardado ya no existe, cae al
          // default de abajo.
          if (prevLeague) return prev;
        }
        // Sin selección guardada o ID ya no existe → default a enabled.
        const enabledLeagues = leagues.filter((l) => l.status === 'enabled');
        // Una sola enabled → auto-seleccionar.
        if (enabledLeagues.length === 1) return enabledLeagues[0].league_id;
        // Ninguna enabled → usar cualquiera.
        if (enabledLeagues.length === 0) return leagues[0]?.league_id ?? null;
        // Varias enabled → dejar null para que el LeaguePicker decida.
        return null;
      });
    } catch (error) {
      console.error('[LeagueProvider] Error fetching admin leagues:', error);
      setAvailableLeagues([]);
    } finally {
      setIsLoading(false);
    }
  }, [adminSessionToken]);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    try {
      if (currentLeagueId) {
        localStorage.setItem(STORAGE_KEY, currentLeagueId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage puede fallar en modo privado; seguimos sin persistir.
    }
  }, [currentLeagueId]);

  const setCurrentLeague = useCallback((leagueId: string) => {
    setCurrentLeagueId(leagueId);
  }, []);

  const currentLeague = useMemo(
    () => availableLeagues.find((l) => l.league_id === currentLeagueId) ?? null,
    [availableLeagues, currentLeagueId]
  );

  const value = useMemo<LeagueContextValue>(() => ({
    availableLeagues,
    currentLeague,
    setCurrentLeague,
    refreshLeagues: fetchLeagues,
    isLoading,
    adminSessionToken,
  }), [availableLeagues, currentLeague, setCurrentLeague, fetchLeagues, isLoading, adminSessionToken]);

  return <LeagueContext.Provider value={value}>{children}</LeagueContext.Provider>;
};

export const useCurrentLeague = (): LeagueContextValue => {
  const ctx = useContext(LeagueContext);
  if (ctx === undefined) {
    throw new Error('useCurrentLeague debe usarse dentro de <LeagueProvider>');
  }
  return ctx;
};

/**
 * Variante segura: si no estamos dentro de <LeagueProvider>, devuelve null.
 * Útil para hooks compartidos que pueden ser invocados desde flujos
 * pre-login o públicos donde el LeagueProvider no está montado.
 * v6.7.9
 */
export const useOptionalCurrentLeague = (): LeagueContextValue | null => {
  const ctx = useContext(LeagueContext);
  return ctx ?? null;
};
